/* iter368 — Public AI Systems Transparency Page.
 *
 * Human-friendly view of the EU AI Act Art. 49 register. Renders the JSON
 * from GET /api/me/gdpr/ai-systems/register as a searchable card grid.
 * No auth required — intended for regulators, auditors, B2B prospects.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import {
  Shield, Cpu, Search, Loader2, Mail, AlertTriangle, ExternalLink,
  BookOpen, Microscope, Brain,
} from "lucide-react";

const RISK_COLORS = {
  minimal:  "bg-emerald-100 text-emerald-700 border-emerald-200",
  limited:  "bg-blue-100 text-blue-700 border-blue-200",
  high:     "bg-amber-100 text-amber-700 border-amber-200",
  unacceptable: "bg-red-100 text-red-700 border-red-200",
};

function riskClassToColor(rc = "") {
  const s = rc.toLowerCase();
  if (s.includes("minimal")) return RISK_COLORS.minimal;
  if (s.includes("high"))    return RISK_COLORS.high;
  if (s.includes("limited")) return RISK_COLORS.limited;
  return RISK_COLORS.limited;
}

function iconForSystem(id = "") {
  if (id.includes("oncology"))     return Microscope;
  if (id.includes("llm") || id.includes("explanation") || id.includes("vision") || id.includes("voice")) return Brain;
  return Cpu;
}

export default function PublicAISystemsPage() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [register, setRegister] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    axios.get(`${API}/me/gdpr/ai-systems/register`)
      .then(({ data }) => setRegister(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [API]);

  const filtered = (register?.systems || []).filter((s) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return s.id.toLowerCase().includes(q)
        || s.name.toLowerCase().includes(q)
        || (s.purpose || "").toLowerCase().includes(q)
        || (s.model_kind || "").toLowerCase().includes(q);
  });

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(222,47%,8%)] text-gray-100" : "bg-gray-50 text-gray-900"}`} data-testid="public-ai-systems-page">
      <SEO
        title={bg ? "AI System Register — Nutramilo" : "AI System Register — Nutramilo"}
        description={bg
          ? "Прозрачен регистър на всички AI системи на Nutramilo съгласно EU AI Act чл. 49"
          : "Transparent register of all AI systems used by Nutramilo per EU AI Act Art. 49"}
      />

      <header className={`border-b ${isDark ? "border-white/10 bg-[hsl(222,47%,10%)]" : "border-gray-200 bg-white"}`}>
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-3">
            <Shield className="w-7 h-7 text-emerald-500" />
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              {bg ? "Регистър на AI системите" : "AI System Register"}
            </h1>
          </div>
          <p className={`text-sm leading-relaxed max-w-3xl ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            {bg
              ? "Прозрачен публичен регистър на всички AI системи в платформата Nutramilo съгласно Регламент (ЕС) 2024/1689 (EU AI Act), член 49 + Анекс VIII. Тук намирате какво прави всяка наша AI компонента, какъв е нейният рисков клас, на каква литература е базирана и към кого да отправите жалба."
              : "Transparent public register of all AI systems in the Nutramilo platform under Regulation (EU) 2024/1689 (EU AI Act), Art. 49 + Annex VIII. See what each AI component does, its risk class, the literature it is based on, and the complaint contact."}
          </p>

          {register && (
            <div className={`grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 text-[12px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              <Meta label={bg ? "Версия" : "Version"} value={register.version} isDark={isDark} />
              <Meta label={bg ? "Актуализиран" : "Updated"} value={register.last_updated} isDark={isDark} />
              <Meta label={bg ? "Оператор" : "Operator"} value={register.operator?.split("(")[0].trim()} isDark={isDark} />
              <Meta label={bg ? "Системи" : "Systems"} value={register.systems_count} isDark={isDark} />
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Search */}
        <div className={`relative flex items-center rounded-xl border px-3 py-2 ${isDark ? "bg-white/5 border-white/10" : "bg-white border-gray-200"}`}>
          <Search className="w-4 h-4 opacity-60 mr-2" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={bg ? "Търсене по име, цел или модел..." : "Search by name, purpose or model..."}
            className="flex-1 bg-transparent outline-none text-sm"
            data-testid="ai-systems-search"
          />
          {query && (
            <button onClick={() => setQuery("")} className="text-xs opacity-60 hover:opacity-100">
              {bg ? "Изчисти" : "Clear"}
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-7 h-7 animate-spin text-emerald-500" />
          </div>
        ) : !register ? (
          <div className="text-center py-16 text-sm opacity-60">
            {bg ? "Грешка при зареждане" : "Failed to load"}
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ai-systems-grid">
            {filtered.map((s) => {
              const Icon = iconForSystem(s.id);
              return (
                <li
                  key={s.id}
                  className={`rounded-xl border p-4 ${isDark ? "bg-white/5 border-white/10" : "bg-white border-gray-200"}`}
                  data-testid={`ai-system-card-${s.id}`}
                >
                  <div className="flex items-start gap-3">
                    <Icon className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-500" />
                    <div className="min-w-0 flex-1">
                      <h3 className="text-[14px] font-semibold leading-snug">{s.name}</h3>
                      <p className={`text-[11px] mt-0.5 font-mono break-all ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                        {s.id}
                      </p>
                      <p className={`text-[13px] leading-relaxed mt-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        {s.purpose}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${riskClassToColor(s.risk_class)}`}>
                          {s.risk_class}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] border ${isDark ? "bg-white/5 border-white/10 text-gray-400" : "bg-gray-50 border-gray-200 text-gray-600"}`}>
                          {s.model_kind}
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Resources */}
        {register && (
          <section className={`rounded-xl border p-5 ${isDark ? "bg-white/5 border-white/10" : "bg-white border-gray-200"}`}>
            <h2 className="text-base font-bold flex items-center gap-2 mb-3">
              <BookOpen className="w-4 h-4 text-emerald-500" />
              {bg ? "Документи и контакти" : "Documents & contacts"}
            </h2>
            <ul className="space-y-2 text-[13px]">
              <li className="flex items-start gap-2">
                <Mail className="w-3.5 h-3.5 mt-1 opacity-70" />
                <div>
                  <span className={`${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {bg ? "Контакт AI системи:" : "AI systems contact:"}
                  </span>
                  <a href={`mailto:${register.contact}`} className="ml-1.5 text-emerald-500 hover:underline" data-testid="contact-ai-systems">
                    {register.contact}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-1 text-amber-500" />
                <div>
                  <span className={`${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {bg ? "Жалби:" : "Complaints:"}
                  </span>
                  <a href={`mailto:${register.complaint_contact}`} className="ml-1.5 text-emerald-500 hover:underline" data-testid="contact-complaint">
                    {register.complaint_contact}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <ExternalLink className="w-3.5 h-3.5 mt-1 opacity-70" />
                <div>
                  <span className={`${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {bg ? "Надзорен орган:" : "Supervisory authority:"}
                  </span>
                  <a href="https://www.cpdp.bg" target="_blank" rel="noopener noreferrer"
                      className="ml-1.5 text-emerald-500 hover:underline" data-testid="link-cpdp-public">
                    {register.supervisory_authority_bg}
                  </a>
                </div>
              </li>
              <li className={`text-[11px] mt-3 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                {register.regulatory_basis}
              </li>
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}

function Meta({ label, value, isDark }) {
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-wider opacity-60`}>{label}</div>
      <div className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{value || "—"}</div>
    </div>
  );
}
